import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';

import { AppState } from 'src';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { PageViewModel } from '@models/common.models';
import { SwoPayoutViewModel } from '@models/SwoPayout';
import { swoPayoutSerivce, SwoPayoutSearchRequest } from '@services/SwoPayoutService';
import { CilOfferState } from './cil-offer.reducer';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CilOfferActionType {
  /********** Approve **********/
  ApproveCilOfferFailure = 'ApproveCilOfferFailure',
  ApproveCilOfferStart = 'ApproveCilOfferStart',
  ApproveCilOfferSuccess = 'ApproveCilOfferSuccess',
  /********** Delete **********/
  DeleteCilOfferFailure = 'DeleteCilOfferFailure',
  DeleteCilOfferStart = 'DeleteCilOfferStart',
  DeleteCilOfferSuccess = 'DeleteCilOfferSuccess',
  /********** Search **********/
  SearchCilOffersFailure = 'SearchCilOffersFailure',
  SearchCilOffersStart = 'SearchCilOffersStart',
  SearchCilOffersSuccess = 'SearchCilOffersSuccess'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Approve **********/
export interface ApproveCilOfferFailure extends Action {
  readonly type: CilOfferActionType.ApproveCilOfferFailure;
}
export interface ApproveCilOfferStart extends Action {
  readonly type: CilOfferActionType.ApproveCilOfferStart;
}
export interface ApproveCilOfferSuccess extends Action {
  readonly type: CilOfferActionType.ApproveCilOfferSuccess;
}

/********** Delete **********/
export interface DeleteCilOfferFailure extends Action {
  readonly type: CilOfferActionType.DeleteCilOfferFailure;
}
export interface DeleteCilOfferStart extends Action {
  readonly type: CilOfferActionType.DeleteCilOfferStart;
}
export interface DeleteCilOfferSuccess extends Action {
  readonly type: CilOfferActionType.DeleteCilOfferSuccess;
}

/********** Search **********/
export interface SearchCilOffersFailureAction extends Action {
  readonly type: CilOfferActionType.SearchCilOffersFailure;
  readonly errorMessage: string;
}
export interface SearchCilOffersStartAction extends Action {
  readonly type: CilOfferActionType.SearchCilOffersStart;
  readonly orderBy: string;
  readonly orderByDirection: 'asc' | 'desc';
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly query: string | null;
}
export interface SearchCilOffersSuccessAction extends Action {
  readonly type: CilOfferActionType.SearchCilOffersSuccess;
  readonly searchResult: PageViewModel<SwoPayoutViewModel>;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Approve **********/
export const approveCilOffer = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(approveCilOfferStart());
    try {
      await swoPayoutSerivce.approve(id);
      dispatch(approveCilOfferSuccess());
    } catch (e) {
      dispatch(approveCilOfferFailure(e.toString()));
    }
  };
};
export function approveCilOfferFailure(errorMessage: string): ApproveCilOfferFailure {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Error, unable to approve CIL offer. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: CilOfferActionType.ApproveCilOfferFailure
  };
}
export function approveCilOfferStart(): ApproveCilOfferStart {
  return {
    type: CilOfferActionType.ApproveCilOfferStart
  };
}
export function approveCilOfferSuccess(): ApproveCilOfferSuccess {
  return {
    type: CilOfferActionType.ApproveCilOfferSuccess
  };
}

/********** Delete **********/
export const deleteCilOffer = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(deleteCilOfferStart());
    try {
      await swoPayoutSerivce.delete(id);
      dispatch(deleteCilOfferSuccess());
    } catch (e) {
      dispatch(deleteCilOfferFailure(e.toString()));
    }
  };
};
export function deleteCilOfferFailure(errorMessage: string): DeleteCilOfferFailure {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Error, unable to delete CIL offer. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: CilOfferActionType.DeleteCilOfferFailure
  };
}
export function deleteCilOfferStart(): DeleteCilOfferStart {
  return {
    type: CilOfferActionType.DeleteCilOfferStart
  };
}
export function deleteCilOfferSuccess(): DeleteCilOfferSuccess {
  return {
    type: CilOfferActionType.DeleteCilOfferSuccess
  };
}

/********** Search **********/
export const searchCilOffers = (
  orderBy: string,
  orderByDirection: 'asc' | 'desc',
  pageNumber: number,
  pageSize: number,
  query: string | null
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchCilOffersStart(orderBy, orderByDirection, pageNumber, pageSize, query));
    try {
      let swoId: number | undefined = undefined;
      if (query != null && query.length > 0) {
        swoId = parseInt(query, 10);
        swoId = isNaN(swoId) ? undefined : swoId;
      }
      const request: SwoPayoutSearchRequest = {
        orderBy: orderBy,
        orderByDirection: orderByDirection,
        pageNumber: pageNumber,
        pageSize: pageSize,
        hasInvoice: false,
        swoIds: swoId == null ? undefined : [swoId]
      };
      const searchResult: PageViewModel<SwoPayoutViewModel> = await swoPayoutSerivce.search(request);
      dispatch(searchCilOffersSuccess(searchResult));
    } catch (e) {
      dispatch(searchCilOffersFailure(e.toString()));
    }
  };
};
export const refreshCilOffers = () => {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state: CilOfferState = getState().cilOffer;
    await dispatch(
      searchCilOffers(
        state.searchOrderBy,
        state.searchOrderByDirection,
        state.searchPageNumber,
        state.searchPageSize,
        state.searchQuery
      )
    );
  };
};
export function searchCilOffersFailure(errorMessage: string): SearchCilOffersFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search CIL offers. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: CilOfferActionType.SearchCilOffersFailure,
    errorMessage: errorMessage
  };
}
export function searchCilOffersStart(
  orderBy: string,
  orderByDirection: 'asc' | 'desc',
  pageNumber: number,
  pageSize: number,
  query: string | null
): SearchCilOffersStartAction {
  return {
    type: CilOfferActionType.SearchCilOffersStart,
    orderBy: orderBy,
    orderByDirection: orderByDirection,
    pageNumber: pageNumber,
    pageSize: pageSize,
    query: query
  };
}
export function searchCilOffersSuccess(result: PageViewModel<SwoPayoutViewModel>): SearchCilOffersSuccessAction {
  return {
    type: CilOfferActionType.SearchCilOffersSuccess,
    searchResult: result
  };
}



// WEBPACK FOOTER //
// ./src/cil-offer/cil-offer.actions.tsx